import { useEffect, useState } from 'react';
import { Alert, Button, Center, Container, Loader, Paper, Stack, Text, Title } from '@mantine/core';
import { Link, useNavigate } from 'react-router-dom';
import { getBrowserSessionId } from '../auth/browserSession';
import { saveSessionExpiry, verifyMagicLink } from '../api/auth';
import { ApiError } from '../api/http';
import PageShell from '../components/PageShell';
import { requestDraftRestore } from '../hooks/useFormDraft';
import { clearReturnTo, extractSiteType, getReturnTo } from '../hooks/returnTo';
import { useResolvedProgram } from '../hooks/useResolvedProgram';
import { buildPortalDestination } from '../utils/portalPath';

const pendingTokenStorageKey = 'microsites-pending-magic-link-token';
const verifyRequestCache = new Map<string, Promise<{ returnTo?: string; pendingLogin?: boolean }>>();

function verifyMagicLinkOnce(token: string, sessionScopeKey: string): Promise<{ returnTo?: string; pendingLogin?: boolean }> {
  const requestKey = `${sessionScopeKey}:${token}`;
  const existingRequest = verifyRequestCache.get(requestKey);
  if (existingRequest) {
    return existingRequest;
  }

  const request = verifyMagicLink(token, getBrowserSessionId()).then((result) => {
    saveSessionExpiry(sessionScopeKey, result.expiresAt);
    return {
      returnTo: result.returnTo,
      pendingLogin: result.pendingLogin,
    };
  });
  verifyRequestCache.set(requestKey, request);
  return request;
}

function getReasonMessage(reason: string | null): string {
  switch (reason) {
    case 'invalid-link':
      return 'That sign-in link is invalid or has expired. Request a new link to continue.';
    case 'wrong-browser':
      return 'Open this sign-in link in the same browser where you requested it. If that is not possible, request a new link and try again.';
    case 'path-mismatch':
      return 'This sign-in link belongs to a different site. Reopen the original link from your email or navigate back to the correct site and try again.';
    case 'verification-failed':
      return 'We could not complete sign-in. Request a new link and try again.';
    default:
      return 'We could not complete sign-in. Request a new link and try again.';
  }
}

export default function AuthVerifyPage() {
  const { data: config, isLoading, error, resolvedProgram, sessionScopeKey } = useResolvedProgram();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'pending' | 'success' | 'error'>('pending');
  const [reason, setReason] = useState<string | null>(null);
  const [serverReturnTo, setServerReturnTo] = useState<string | undefined>();

  useEffect(() => {
    if (config?.pages?.login?.title) {
      document.title = config.pages.login.title;
    }
  }, [config?.pages?.login?.title]);

  useEffect(() => {
    if (status === 'success') {
      const timer = window.setTimeout(() => {
        void (async () => {
          // Backend already handles terms check and issues pendingLogin when needed
          // Just proceed to destination when status is 'success'
          const returnTo = serverReturnTo ?? getReturnTo();
          const returnSiteType = extractSiteType(returnTo);
          if (returnSiteType && resolvedProgram) {
            requestDraftRestore(resolvedProgram.draftScopeKeys[returnSiteType]);
          }
          clearReturnTo();
          window.location.replace(buildPortalDestination(returnTo));
        })();
      }, 150);

      return () => window.clearTimeout(timer);
    }

    return undefined;
  }, [resolvedProgram, serverReturnTo, status]);

  useEffect(() => {
    const hash = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : window.location.hash;
    const hashParams = new URLSearchParams(hash);
    const tokenFromHash = hashParams.get('token')?.trim() ?? '';
    const token = tokenFromHash || sessionStorage.getItem(pendingTokenStorageKey)?.trim() || '';

    if (tokenFromHash) {
      sessionStorage.setItem(pendingTokenStorageKey, tokenFromHash);
      window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
    }

    if (!token) {
      setStatus('error');
      setReason('invalid-link');
      return undefined;
    }

    if (!sessionScopeKey) {
      return undefined;
    }

    let cancelled = false;
    const requestKey = `${sessionScopeKey}:${token}`;

    void verifyMagicLinkOnce(token, sessionScopeKey)
      .then((result) => {
        if (cancelled) {
          return;
        }
        sessionStorage.removeItem(pendingTokenStorageKey);
        verifyRequestCache.delete(requestKey);
        
        // Check if terms need to be accepted (backend sends pendingLogin: true)
        if (result.pendingLogin) {
          console.log('Pending login detected, redirecting to terms');
          const returnTo = result.returnTo ?? getReturnTo() ?? '/';
          clearReturnTo();
          navigate(`/auth/terms?returnTo=${encodeURIComponent(returnTo)}`, { replace: true });
          return;
        }
        
        console.log('Normal login flow, setting success');
        setServerReturnTo(result.returnTo);
        setStatus('success');
        setReason(null);
      })
      .catch((err: unknown) => {
        if (cancelled) {
          return;
        }

        verifyRequestCache.delete(requestKey);

        if (!(err instanceof ApiError && err.message === 'path-mismatch')) {
          sessionStorage.removeItem(pendingTokenStorageKey);
        }

        if (err instanceof ApiError && (err.status === 401 || err.status === 409)) {
          if (err.message === 'wrong-browser' || err.message === 'path-mismatch') {
            setReason(err.message);
          } else {
            setReason('invalid-link');
          }
        } else {
          setReason('verification-failed');
        }
        setStatus('error');
      });

    return () => {
      cancelled = true;
    };
  }, [sessionScopeKey]);

  if (isLoading) {
    return (
      <Center h="100vh">
        <Loader size="lg" />
      </Center>
    );
  }

  if (error || !config) {
    return (
      <Container size="sm" pt="xl">
        <Alert color="red" title="Configuration Error">
          Unable to load form configuration. Please try refreshing the page.
        </Alert>
      </Container>
    );
  }

  if (status === 'success') {
    return (
      <PageShell
        config={config}
        headerLogoUrl={config.pages?.login?.logoUrl ?? config.theme.logoUrl}
        headerTitle={config.pages?.login?.title ?? config.theme.displayName}
        support={config.pages?.verify?.showSupportInfo === true ? config.support : undefined}
      >
        <Paper p="lg" withBorder radius="md" className="portal-panel">
          <Stack align="center" gap="sm">
            <Loader size="md" />
            <Title order={4} className="portal-section-title">
              Sign-in successful
            </Title>
            <Text size="sm" c="dimmed" ta="center">
              Redirecting you back to your previous page.
            </Text>
          </Stack>
        </Paper>
      </PageShell>
    );
  }

  if (status === 'pending') {
    return (
      <PageShell
        config={config}
        headerLogoUrl={config.pages?.login?.logoUrl ?? config.theme.logoUrl}
        headerTitle={config.pages?.login?.title ?? config.theme.displayName}
        support={config.pages?.verify?.showSupportInfo === true ? config.support : undefined}
      >
        <Paper p="lg" withBorder radius="md" className="portal-panel">
          <Stack align="center" gap="sm">
            <Loader size="md" />
            <Title order={4} className="portal-section-title">
              Completing sign-in
            </Title>
            <Text size="sm" c="dimmed" ta="center">
              Verifying your secure sign-in link.
            </Text>
          </Stack>
        </Paper>
      </PageShell>
    );
  }

  return (
    <PageShell
      config={config}
      headerLogoUrl={config.pages?.login?.logoUrl ?? config.theme.logoUrl}
      headerTitle={config.pages?.login?.title ?? config.theme.displayName}
      support={config.pages?.verify?.showSupportInfo === true ? config.support : undefined}
    >
      <Paper p="lg" withBorder radius="md" className="portal-panel">
        <Stack gap="md">
          <Title order={4} className="portal-section-title">
            Unable to complete sign-in
          </Title>
          <Alert color="red" title="Sign-in link problem">
            {getReasonMessage(reason)}
          </Alert>
          <Button component={Link} to="/auth/request" className="portal-primary-button">
            Request a new sign-in link
          </Button>
        </Stack>
      </Paper>
    </PageShell>
  );
}